import { useRef, useState } from "react";
import { Typography, Paper, Divider, Box, Stack, Button } from "@mui/material";
import Chart from "react-apexcharts";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { useSnackbar } from "notistack";

import DateUtils from "utils/DateUtils.js";
import { getProgressChartGoalLevel } from "utils/AssessmentProgressGoalUtils.js";
import SnackbarConstants from "utils/constants/Snackbar.js";
import DoctorFormService from "services/DoctorFormService";
import { downloadElementAsPdf } from "utils/AssessmentReportPdfUtils";
import MelilloReportHeader from "./MelilloReportHeader";

function SectionProgressChart({ sectionName, dataPoints, patientDob }) {
  const validPoints = dataPoints.filter((p) => p.level != null);

  const categories = validPoints.map((p) => `#${p.assessment}`);
  const levels = validPoints.map((p) => Number(p.level));
  const goalLevel = getProgressChartGoalLevel(sectionName, patientDob);
  const maxLevel = levels.length > 0 ? Math.max(...levels) : 0;
  const yMax = Math.max(maxLevel, goalLevel, 4);

  const chartId = `assess-chart-${sectionName
    .replace(/[^a-zA-Z0-9]/g, "-")
    .toLowerCase()}`;

  const lineColor = "#1565c0";
  const fillTo = "#90caf9";

  const options = {
    chart: {
      id: chartId,
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit",
      background: "#ffffff",
    },
    legend: { show: false },
    stroke: {
      curve: "smooth",
      width: 3,
      lineCap: "round",
    },
    annotations: {
      yaxis: [
        {
          y: goalLevel,
          borderColor: "#e65100",
          strokeDashArray: 6,
          label: {
            borderColor: "#e65100",
            style: {
              color: "#fff",
              background: "#e65100",
              fontSize: "11px",
              fontWeight: 600,
            },
            text: "Goal",
          },
        },
      ],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.15,
        gradientToColors: [fillTo],
        inverseColors: false,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },
    markers: {
      size: 6,
      strokeWidth: 2,
      strokeColors: "#fff",
      hover: { sizeOffset: 2 },
      colors: [lineColor],
    },
    colors: [lineColor],
    xaxis: {
      categories,
      axisBorder: { show: true, color: "#cfd8dc" },
      axisTicks: { show: true, color: "#cfd8dc" },
      title: {
        text: "Assessment Number",
        offsetY: 6,
        style: { fontSize: "13px", fontWeight: 600, color: "#455a64" },
      },
      labels: {
        style: { fontSize: "11px", colors: "#546e7a" },
      },
    },
    yaxis: {
      min: 0,
      max: yMax,
      tickAmount: Math.min(yMax + 1, 11),
      title: {
        text: "Level",
        rotate: -90,
        style: { fontSize: "13px", fontWeight: 600, color: "#455a64" },
      },
      labels: {
        formatter: (v) => (Number.isInteger(v) ? String(v) : ""),
        style: { fontSize: "11px", colors: "#546e7a" },
        maxWidth: 40,
      },
    },
    tooltip: {
      theme: "light",
      custom: ({ dataPointIndex }) => {
        const point = validPoints[dataPointIndex];
        if (!point) {
          return "";
        }
        return `<div style="padding:8px 12px;font-size:13px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.12);">
          <strong style="color:#1565c0;">Assessment ${point.assessment}</strong><br/>
          <span style="color:#455a64;">Level:</span> <strong>${point.level}</strong>
        </div>`;
      },
    },
    grid: {
      borderColor: "#eceff1",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 4, right: 12, bottom: 0, left: 28 },
    },
  };

  const series = [{ name: "Level", data: levels }];

  return (
    <Box data-apex-chart-id={chartId}>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          fontWeight: 700,
          color: "primary.dark",
          mb: 1,
          lineHeight: 1.35,
        }}
      >
        {sectionName}
      </Typography>
      <Box
        data-apex-chart-live
        sx={{
          width: "100%",
          "& .apexcharts-canvas": { width: "100% !important" },
          "& .apexcharts-yaxis text": { fill: "#546e7a" },
          "& .apexcharts-xaxis text": { fill: "#546e7a" },
          "& .apexcharts-yaxis-title text": { fill: "#455a64 !important" },
        }}
      >
        <Chart
          options={options}
          series={series}
          type="area"
          height={380}
          width="100%"
        />
      </Box>
    </Box>
  );
}

function AssessmentReportBody({ reportData, targetRef }) {
  const { firstName, lastName, evaluationDate, sections = {}, patientDob } =
    reportData;

  const sectionEntries = Object.entries(sections).filter(
    ([, dataPoints]) => Array.isArray(dataPoints) && dataPoints.length > 0
  );

  return (
    <Paper ref={targetRef} className="p-3" elevation={2} sx={{ px: { xs: 2, md: 3 } }}>
      <MelilloReportHeader
        title="ASSESSMENT PROGRESS REPORT"
        firstName={firstName}
        lastName={lastName}
        evaluationDate={evaluationDate}
      />

      <main>
        <section className="pt-3 pb-2">
          <Typography variant="h3">Melillo Method 10 Level System</Typography>
          <Typography variant="body1" className="mt-2">
            This progress report shows how {firstName}&apos;s levels have changed
            across assessment sessions for each evaluated section. Charts plot the
            level recorded at successive assessments, with a goal line based on
            age or functional target for that section.
          </Typography>
        </section>

        <Divider />

        <section className="pt-3 pb-1">
          <Typography variant="h3" className="mb-2">
            Progress by Section
          </Typography>

          {sectionEntries.length === 0 ? (
            <Typography variant="body1">No progress data available.</Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {sectionEntries.map(([sectionName, dataPoints], idx) => (
                <Box
                  key={`section-${idx}`}
                  sx={{
                    flex: "1 1 calc(50% - 8px)",
                    maxWidth: { xs: "100%", sm: "calc(50% - 8px)" },
                    minWidth: 0,
                    p: { xs: 1.5, md: 2 },
                    borderRadius: 1,
                    border: "1px solid rgba(0,0,0,0.12)",
                    bgcolor: "#fff",
                  }}
                >
                  <SectionProgressChart
                    sectionName={sectionName}
                    dataPoints={dataPoints}
                    patientDob={patientDob}
                  />
                </Box>
              ))}
            </Box>
          )}
        </section>

        <Typography
          variant="body2"
          className="mt-3"
          sx={{ fontStyle: "italic", color: "#555" }}
        >
          Generated on {DateUtils.formatDateTimeToDate(new Date())}. Please
          contact your care team with any questions about this report.
        </Typography>
      </main>
    </Paper>
  );
}

const AssessmentFormReport = ({
  reportData = {},
  hideEmailButton = false,
  hospitalId,
  patientId,
}) => {
  const targetRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      enqueueSnackbar("Generating PDF. Please wait...", SnackbarConstants.info);
      const fileName =
        `Assessment_Progress_Report_${reportData.firstName}_${reportData.lastName || ""}`.replace(
          /\s+/g,
          "_"
        );
      await downloadElementAsPdf(targetRef.current, fileName, {
        replaceApexCharts: true,
      });
      enqueueSnackbar("PDF downloaded successfully", SnackbarConstants.success);
    } catch (error) {
      enqueueSnackbar("Failed to generate PDF", SnackbarConstants.error);
    }
  };

  const handleEmailPatient = async () => {
    try {
      setSending(true);
      enqueueSnackbar(
        "Sending progress report notification to patient...",
        SnackbarConstants.info
      );
      await DoctorFormService.emailProgressReport([
        hospitalId,
        patientId,
        reportData.id,
      ]);
      enqueueSnackbar(
        "Progress report notification sent to patient successfully",
        SnackbarConstants.success
      );
    } catch (err) {
      enqueueSnackbar(
        "Failed to send notification email",
        SnackbarConstants.error
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper elevation={0} className="py-2 px-2">
      <div
        className="d-flex justify-content-between p-3 align-items-center"
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "#e6f0ff",
          borderRadius: 5,
          zIndex: 1,
        }}
      >
        <Typography variant="h2">Assessment Progress Report</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            type="button"
            startIcon={<PictureAsPdfRoundedIcon />}
            onClick={handleDownloadPdf}
            variant="outlined"
          >
            Download PDF
          </Button>
          {!hideEmailButton && (
            <Button
              startIcon={<MailOutlineRoundedIcon />}
              variant="contained"
              onClick={handleEmailPatient}
              disabled={sending}
            >
              Share to Patient
            </Button>
          )}
        </Stack>
      </div>

      <AssessmentReportBody reportData={reportData} targetRef={targetRef} />
    </Paper>
  );
};

export default AssessmentFormReport;
